import React, { useState, useEffect } from 'react';
import {
  Building2, Plus, Trash2, Edit2, Loader2, Check, X,
  Eye, EyeOff, GripVertical, Save, RotateCcw, HardHat
} from 'lucide-react';
import { installateursAPI } from '../../services/api';

const WidgetInstallateursConfig = () => {
  const [installateurs, setInstallateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [togglingVisibility, setTogglingVisibility] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [newNom, setNewNom] = useState('');

  useEffect(() => {
    loadInstallateurs();
  }, []);

  const loadInstallateurs = async () => {
    try {
      setLoading(true);
      const res = await installateursAPI.getAll(true);
      setInstallateurs(res.data || []);
    } catch (error) {
      console.error('Erreur chargement installateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newNom.trim()) return;

    setSaving(true);
    try {
      await installateursAPI.create({
        nom: newNom.trim(),
        ordre: installateurs.length
      });
      await loadInstallateurs();
      setNewNom('');
      setShowAddForm(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet installateur ?')) return;

    setDeleting(id);
    try {
      await installateursAPI.delete(id);
      await loadInstallateurs();
    } catch (error) {
      alert('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleVisibility = async (inst) => {
    setTogglingVisibility(inst.id);
    try {
      await installateursAPI.update(inst.id, { visible: !inst.visible });
      await loadInstallateurs();
    } catch (error) {
      alert('Erreur lors de la modification');
    } finally {
      setTogglingVisibility(null);
    }
  };

  const startEditing = (inst) => {
    setEditingId(inst.id);
    setEditingName(inst.nom);
  };

  const saveEditing = async () => {
    if (!editingName.trim() || !editingId) return;

    setSaving(true);
    try {
      await installateursAPI.update(editingId, { nom: editingName.trim() });
      await loadInstallateurs();
      setEditingId(null);
      setEditingName('');
    } catch (error) {
      alert('Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
  };

  // Drag & Drop
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newList = [...installateurs];
    const draggedItem = newList[draggedIndex];
    newList.splice(draggedIndex, 1);
    newList.splice(index, 0, draggedItem);

    newList.forEach((item, idx) => {
      item.ordre = idx;
    });

    setInstallateurs(newList);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      for (let i = 0; i < installateurs.length; i++) {
        await installateursAPI.update(installateurs[i].id, { ordre: i });
      }
      setHasChanges(false);
    } catch (error) {
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const visibleCount = installateurs.filter(i => i.visible !== false).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <HardHat className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Installateurs / Artisans</h2>
              <p className="text-indigo-100">Gérez vos artisans partenaires fréquents</p>
            </div>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
            {visibleCount}/{installateurs.length} visibles
          </span>
        </div>
      </div>

      {/* Liste des installateurs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-4">
            Ajoutez les artisans avec qui vous travaillez souvent. Ils apparaîtront dans la liste de sélection sur les fiches dossier.
          </p>

          <div className="space-y-2 mb-4">
            {installateurs.map((inst, index) => (
              <div
                key={inst.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition cursor-move ${
                  draggedIndex === index
                    ? 'border-indigo-400 bg-indigo-50 shadow-lg'
                    : inst.visible !== false
                      ? 'border-indigo-200 bg-indigo-50 hover:border-indigo-300'
                      : 'border-gray-200 bg-gray-100 hover:border-gray-300'
                }`}
              >
                {/* Handle */}
                <div className="text-gray-400 hover:text-gray-600">
                  <GripVertical className="w-5 h-5" />
                </div>

                {/* Icône */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  inst.visible !== false ? 'bg-indigo-100' : 'bg-gray-200'
                }`}>
                  <Building2 className={`w-5 h-5 ${inst.visible !== false ? 'text-indigo-600' : 'text-gray-500'}`} />
                </div>

                {/* Nom */}
                <div className="flex-1">
                  {editingId === inst.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="flex-1 px-2 py-1 border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEditing();
                          if (e.key === 'Escape') cancelEditing();
                        }}
                      />
                      <button
                        onClick={saveEditing}
                        disabled={saving}
                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <span className={`font-medium ${inst.visible !== false ? 'text-gray-900' : 'text-gray-500'}`}>
                        {inst.nom}
                      </span>
                      {inst.visible === false && (
                        <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                          Masqué
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {editingId !== inst.id && (
                    <button
                      onClick={() => startEditing(inst)}
                      className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                      title="Modifier"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleToggleVisibility(inst)}
                    disabled={togglingVisibility === inst.id}
                    className={`p-2 rounded-lg transition ${
                      inst.visible !== false
                        ? 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                        : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                    }`}
                    title={inst.visible !== false ? 'Masquer' : 'Afficher'}
                  >
                    {togglingVisibility === inst.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : inst.visible !== false ? (
                      <Eye className="w-5 h-5" />
                    ) : (
                      <EyeOff className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(inst.id)}
                    disabled={deleting === inst.id}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Supprimer"
                  >
                    {deleting === inst.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}

            {installateurs.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Aucun installateur enregistré</p>
                <p className="text-sm">Ajoutez vos artisans partenaires</p>
              </div>
            )}
          </div>

          {/* Formulaire d'ajout */}
          {showAddForm ? (
            <form onSubmit={handleAdd} className="bg-indigo-50 rounded-lg p-4 border border-indigo-200 mb-4">
              <h4 className="font-semibold text-gray-900 mb-3">➕ Nouvel installateur</h4>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'entreprise *</label>
                <input
                  type="text"
                  value={newNom}
                  onChange={(e) => setNewNom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: Entreprise Dupont"
                  required
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setShowAddForm(false); setNewNom(''); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving || !newNom.trim()}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Créer
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full py-3 border-2 border-dashed border-indigo-300 rounded-lg text-indigo-600 hover:border-indigo-400 hover:bg-indigo-50 transition flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Ajouter un installateur
            </button>
          )}

          {/* Bouton sauvegarder l'ordre */}
          {hasChanges && (
            <div className="flex items-center gap-2 pt-4 border-t border-gray-200 mt-4">
              <button
                onClick={saveOrder}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Enregistrer l'ordre
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  loadInstallateurs();
                  setHasChanges(false);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetInstallateursConfig;
